import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { BellRing, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useCleanerTeamMemberId } from '@/hooks/useCleanerTeamMemberId';
import { cn } from '@/lib/utils';
import type { DashboardJob } from '@/hooks/useDashboardData';

interface CleanerNotificationBarProps {
  orgId: string | null | undefined;
  /** Upcoming + future jobs already loaded by the dashboard (this week → +90d). */
  jobs: DashboardJob[];
}

const VIEWED_RETENTION_MS = 24 * 60 * 60 * 1000;

function jobSortKey(job: DashboardJob): string {
  return `${job.scheduled_date} ${job.start_time}`;
}

function sortRecentJobsFirst(a: DashboardJob, b: DashboardJob): number {
  return jobSortKey(b).localeCompare(jobSortKey(a));
}

function formatJobDate(dateStr: string): string {
  const date = parseISO(dateStr);
  if (isToday(date)) return 'Today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'EEE, MMM d');
}

function normalisedTitle(job: DashboardJob): string {
  return job.title?.trim() || 'Job';
}

function isRescheduledJob(job: DashboardJob): boolean {
  return normalisedTitle(job).toLowerCase().startsWith('rescheduled booking:');
}

function isCancellationApprovedJob(job: DashboardJob): boolean {
  return job.status === 'cancelled' || normalisedTitle(job).toLowerCase().startsWith('cancelled booking:');
}

function displayTitle(job: DashboardJob): string {
  return normalisedTitle(job).replace(/^(rescheduled|cancelled) booking:\s*/i, '') || 'Job';
}

function isCleanerNotificationJob(job: DashboardJob, memberId: string): boolean {
  if (job.team_member_id !== memberId) return false;
  if (isCancellationApprovedJob(job)) return true;
  return ['scheduled', 'confirmed'].includes(job.status);
}

function formatNotificationLabel(job: DashboardJob): string {
  const title = displayTitle(job);
  const date = formatJobDate(job.scheduled_date);
  if (isCancellationApprovedJob(job)) {
    return `Cancelled Order - (${title} - ${date})`;
  }
  return isRescheduledJob(job)
    ? `Rescheduled Order - (${title} - ${date})`
    : `New Job Order (${title} - ${date})`;
}

function notificationKey(job: DashboardJob): string {
  return `${job.id}:${job.updated_at ?? job.scheduled_date}`;
}

interface ViewedNotificationState {
  viewedItems?: Record<string, number>;
}

/**
 * Dashboard notification control shown only to cleaners (staffRole === 'cleaner').
 *
 * By default it renders just a small bell icon button — keeping the dashboard
 * uncluttered. When the cleaner has upcoming assigned jobs (newly assigned orders
 * and schedule changes), a red unread count sits on the icon. Clicking the icon
 * opens a popover listing those jobs; each item links to the calendar/schedule so
 * they can act the moment they log in. Renders nothing for owners/super_admins,
 * and nothing when the cleaner has no upcoming assigned jobs.
 */
export function CleanerNotificationBar({ orgId, jobs }: CleanerNotificationBarProps) {
  const { staffRole } = useAuth();
  const isCleaner = staffRole === 'cleaner';
  const { data: memberId } = useCleanerTeamMemberId(orgId, isCleaner);
  const [viewedState, setViewedState] = useState<ViewedNotificationState>({});

  const myJobs = useMemo(
    () =>
      jobs
        .filter((job) => (memberId ? isCleanerNotificationJob(job, memberId) : false))
        .sort(sortRecentJobsFirst),
    [jobs, memberId]
  );

  const jobKeys = useMemo(() => myJobs.map(notificationKey), [myJobs]);

  const viewedStorageKey = orgId && memberId
    ? `cleaner-notifications-viewed:${orgId}:${memberId}`
    : null;

  useEffect(() => {
    if (!viewedStorageKey) {
      setViewedState({});
      return;
    }

    try {
      const stored = window.localStorage.getItem(viewedStorageKey);
      setViewedState(stored ? JSON.parse(stored) : {});
    } catch {
      setViewedState({});
    }
  }, [viewedStorageKey]);

  if (!isCleaner || !memberId) return null;

  const viewedItems = viewedState.viewedItems ?? {};
  const now = Date.now();
  const visibleJobs = myJobs.filter((job) => {
    const viewedAt = viewedItems[notificationKey(job)];
    return !viewedAt || now - viewedAt < VIEWED_RETENTION_MS;
  });

  if (visibleJobs.length === 0) return null;

  const unreadCount = visibleJobs.filter((job) => !viewedItems[notificationKey(job)]).length;
  const hasUnread = unreadCount > 0;
  const isNotificationUnread = (job: DashboardJob) => !viewedItems[notificationKey(job)];

  const markNotificationViewed = (job: DashboardJob) => {
    if (!viewedStorageKey) return;

    const nextState: ViewedNotificationState = {
      viewedItems: {
        ...(viewedState.viewedItems ?? {}),
        [notificationKey(job)]: Date.now(),
      },
    };
    setViewedState(nextState);
    window.localStorage.setItem(viewedStorageKey, JSON.stringify(nextState));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          data-testid="dashboard-cleaner-notifications-trigger"
          aria-label={
            hasUnread
              ? `Notifications: ${unreadCount} new job orders & schedule changes`
              : 'Notifications viewed'
          }
          className="relative h-9 w-9 rounded-lg border border-border bg-card text-foreground shadow-[var(--adm-shadow-xs)] hover:bg-muted hover:text-foreground [&_svg]:size-4"
        >
          <BellRing className="h-4 w-4" />
          {hasUnread && (
            <span
              data-testid="dashboard-cleaner-notifications-count"
              className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
            >
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-3"
        data-testid="dashboard-cleaner-notifications"
      >
        <div className="flex items-center gap-2">
          <BellRing className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-medium">New job orders &amp; schedule changes</span>
        </div>
        <ul className="mt-2 space-y-1">
          {visibleJobs.map((job) => {
            const isUnread = isNotificationUnread(job);
            return (
            <li key={job.id}>
              <Link
                to={`/admin/schedule?view=week&date=${job.scheduled_date}&job=${job.id}`}
                onClick={() => markNotificationViewed(job)}
                className={cn(
                  "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-primary/10",
                  !isUnread && "text-muted-foreground opacity-60"
                )}
              >
                <span className="flex-1 truncate">
                  <span className={cn("font-medium", isUnread ? "text-foreground" : "text-muted-foreground")}>{formatNotificationLabel(job)}</span>
                </span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </Link>
            </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
